import { useTheme } from "@amx/ux-twoui-theming";
import { IPivotItemProps, Pivot, PivotItem, Stack } from "@fluentui/react";
import { LocationDescriptorObject, LocationState } from "history";
import React from "react";
import { useHistory } from "react-router-dom";
import { useComponentLogger } from "../../shared/logging";
import { AriaLabelEnforcedProps, InteractiveElementProps, validateProps } from "../../shared/props";
import { ComponentValidator, validateComponent } from "../../shared/validation";
import { IconName } from "../../styles/icons";
import { Icon } from "../Icon/Icon";
import { defaultRendererStyle, getTabsStyles, iconStyle } from "./Tabs.styles";

export interface TabsProps extends AriaLabelEnforcedProps {
    /**
     * The key of the selected item.
     */
    readonly selectedKey: string;

    /**
     * When set to true, a separator is rendered between the TabItems and the Tab's content
     */
    readonly separator?: boolean;

    /**
     *  Option to change the variant for styling
     */
    readonly variant?: "default" | "centeredWithBackground";

    /**
     * Optional callback invoked when a Tabs item is changing.
     */
    readonly onChange?: (item: TabItemProps, ev?: React.MouseEvent<HTMLElement>) => void;
}

export interface TabItemProps extends InteractiveElementProps {
    /**
     * Text of the Tabs Item
     */
    readonly text: string;

    /**
     * Optional addition of icon
     */
    readonly icon?: IconName;

    /**
     * Optional addition of a variant for text color
     */
    readonly textColorVariant?: "primary" | "default";

    /**
     * Optional addition for static width for each tab.
     */
    readonly staticWidth?: "128px";

    /**
     * Unique key identifying this item.
     */
    readonly itemKey: string;

    /**
     * Optional React route of the Tabs Item.
     */
    readonly url?: string | LocationDescriptorObject<LocationState>;
}

const tabsChildrenValidator = (child: React.ReactChild | React.ReactFragment | React.ReactPortal): ComponentValidator => (
    () => (!React.isValidElement<TabItemProps>(child) || child.type !== TabItem ?
        "Tabs only accepts children of type TabItem" : undefined)
);

const dataBiProps = "data-bi-props";

export const Tabs: React.FC<TabsProps> = ({
    selectedKey,
    children,
    onChange,
    variant = "default",
    separator = false,
    ...otherProps
}) => {
    useComponentLogger("Tabs", otherProps.id);

    validateProps("Tabs", otherProps);

    const history = useHistory();
    const theme = useTheme();

    const handleClick = (item?: PivotItem, ev?: React.MouseEvent<HTMLElement>): void => {
        const props = item?.props?.headerButtonProps![dataBiProps] as any;

        if (onChange) {
            onChange(props, ev);
        }

        if (props.url) {
            history.push(props.url);
        }
    };

    const mappedChildren = React.Children.map(children, (child) => {
        if (child) {
            validateComponent("Tabs", [tabsChildrenValidator(child)]);

            return TabItem((child as any).props, (child as any).props.itemKey === selectedKey) as React.ReactElement<IPivotItemProps>;
        }

        return;
    });

    const styles = getTabsStyles(theme, separator, variant);

    return (
        <Pivot
            selectedKey={selectedKey}
            onLinkClick={handleClick}
            styles={styles}
            overflowBehavior="menu"
            theme={theme}
            {...otherProps}
        >
            {mappedChildren}
        </Pivot>
    );
};

export const TabItem: React.FC<TabItemProps> = (props, isSelectorKey: boolean) => {
    const {
        text,
        icon,
        textColorVariant = "default",
        staticWidth,
        "aria-label": ariaLabel,
        itemKey,
        children,
        ...otherProps
    } = props;

    validateProps("TabItem", otherProps);
    const theme = useTheme();

    const customRenderer = (
        link?: IPivotItemProps,
        defaultRenderer?: (link?: IPivotItemProps) => JSX.Element | null,
    ): JSX.Element | null => {
        if (!link || !defaultRenderer) {
            return null;
        }

        let styles: React.CSSProperties = { display: "block", width: staticWidth || "auto" };
        if (isSelectorKey) {
            switch (textColorVariant) {
                case "primary":
                    styles = { ...styles, color: theme.palette.themePrimary };
                    break;
                default:
                    break;
            }
        }

        return (
            <Stack horizontal={true} tokens={{ childrenGap: 8 }} style={{ ...styles }}>
                {icon ?
                    <div style={iconStyle} ><Icon iconName={icon} /> </div> : undefined
                }
                <div style={defaultRendererStyle} >{defaultRenderer({ ...link })}</div>
            </Stack>
        );
    };

    const isCustomRender = icon || textColorVariant !== "default" || staticWidth;
    return (
        <PivotItem
            headerText={text}
            ariaLabel={ariaLabel}
            itemKey={itemKey}
            headerButtonProps={{ ...otherProps, [dataBiProps]: props } as any}
            onRenderItemLink={isCustomRender ? customRenderer : undefined}
        >
            {children}
        </PivotItem>
    );
};
